import { useSelector } from 'react-redux';
import type {
  FeatureFlagAnonymousUserDataPayload,
  FeatureFlagUserData,
  FeatureFlagUserDataPayload,
} from './types';
import { getUserDataForFeatureTests } from '~/selectors/user.selectors';
import { v4 as uuid } from 'uuid';

export type UserOnboardingStatus = 'started' | 'finished' | null;

export const USER_ONBOARDING_STATUS_KEY = 'user_onboarding_status';
const ANONYMOUS_USER_ID_KEY = 'shippo_anonymous_user_id';

const makeAnonymousUser = (): FeatureFlagAnonymousUserDataPayload => {
  let anonymousUserId = localStorage.getItem(ANONYMOUS_USER_ID_KEY);

  if (!anonymousUserId) {
    anonymousUserId = uuid();
    localStorage.setItem(ANONYMOUS_USER_ID_KEY, anonymousUserId);
  }

  return {
    id: anonymousUserId,
    type: 'anonymous',
    attributes: {
      environmentName: CONFIG.environmentName,
      anonymous: true,
    },
  };
};

const getUserIdForFeatureFlag = (loggedUserId: number): string => {
  const userOnboardingStatus = localStorage.getItem(
    USER_ONBOARDING_STATUS_KEY,
  ) as UserOnboardingStatus;

  const userHasStartedOnboarding = userOnboardingStatus === 'started';
  const userHasFinishedOnboarding = userOnboardingStatus === 'finished';

  const shouldUseLocalIdForFeatureFlag = userHasStartedOnboarding && !userHasFinishedOnboarding;

  if (shouldUseLocalIdForFeatureFlag) {
    // anonymousUserId is always set on first render
    return localStorage.getItem(ANONYMOUS_USER_ID_KEY)!;
  }

  return loggedUserId.toString();
};

type UseUserDataForFeatureFlagHook = FeatureFlagUserDataPayload;

/**
 * This hook is designed to retrieve user data for feature flagging.
 * - It always returns anonymous user data with a locally stored UUID on the first render.
 * - If the user is logged in:
 *    - It returns logged user data if the user was onboarded through the old onboarding experience
 *      or has completed onboarding for the new experience.
 *    - If the user is logged in and has started the new onboarding experience,
 *      it returns logged user data with a locally stored UUID.
 *
 * @returns {FeatureFlagUserDataPayload} The user data object for feature flagging.
 */
export const useUserDataForFeatureFlag = (): UseUserDataForFeatureFlagHook => {
  const userData: FeatureFlagUserData | null = useSelector(getUserDataForFeatureTests);

  if (!userData) return makeAnonymousUser();

  const { id, ...otherAttributes } = userData;

  return {
    id: getUserIdForFeatureFlag(id),
    type: 'logged',
    attributes: {
      ...otherAttributes,
      environmentName: CONFIG.environmentName,
      anonymous: false,
    },
  };
};
